import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { EnumTranslation } from '../interfaces/enum-translation.interface';
import { Asset } from '@shared/interfaces/asset.interface';
import { AcceptedLanguage } from '@shared/enums/accepted-language.enum';
import { ConfigService } from './config.service';
import { Observable, of, lastValueFrom } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { getAcceptedLanguageEnumFromLanguageCode } from '@shared/utils.service';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  constructor(
    private translateService: TranslateService,
    private configService: ConfigService,
    private httpClient: HttpClient
  ) {}

  public blobToBase64(blob: Blob): Promise<string> {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
    });
  }

  base64ToBlob(
    base64: string,
    type: string = 'application/octet-stream'
  ): Promise<Blob> {
    return fetch(`data:${type};base64,${base64}`).then((res) => res.blob());
  }

  public base64EncodeUnicode(str: string): string {
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
      })
    );
  }

  public async getEnumTranslation<T extends Record<string, string | number>>(
    enumType: T,
    translationPath: string
  ): Promise<EnumTranslation<T>[]> {
    const translations = await lastValueFrom(
      this.translateService.get(translationPath)
    );
    return Object.keys(enumType).map((key) => {
      const val = (enumType as any)[key];
      const enumTranslation: EnumTranslation<T> = {
        value: val,
        displayName: translations[val],
      };
      return enumTranslation;
    });
  }

  public markFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof UntypedFormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  public getAssetDownloadLink(
    asset: Asset,
    language: AcceptedLanguage
  ): string {
    const searchParams = new URLSearchParams();
    searchParams.append('name', asset.name);
    searchParams.append('extension', asset.extension);
    searchParams.append('lang', language);
    return `${this.configService.getApiBaseUrl()}/assets?${searchParams.toString()}`;
  }

  async getMockData(name: string): Promise<{ [key: string]: any } | null | undefined> {
    const MOCKS: string[] = [
      'patient-consent-form',
      'patient-consent-representative-form',
      'patient-consent-representative-add-form',
      'patient-consent-representative-remove-form',
      'patient-revocation-form',
      'affiliate-institution-form',
      'disaffiliate-institution-form',
      'staff-transfer-request-form',
    ];
    if (!MOCKS.includes(name)) {
      return null;
    }
    return await lastValueFrom(
      this.httpClient.get<{ [key: string]: any }>(`/assets/mocks/${name}.json`)
    );
  }

  public getBaseHref(): string {
    return document.querySelector('base')?.href || window.location.origin;
  }

  public conditionalValidator(predicate: (formControl: AbstractControl) => boolean, validator: ValidatorFn | null) {
    return (formControl: AbstractControl) => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate(formControl) && validator) {
        return validator(formControl); 
      }
      return null;
    }
  }

  public validateGlnAsync(formControl: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    const baseUrl: string =
      this.configService.getApiBaseUrl() + '/refdata';
    const searchParams = new URLSearchParams();
    searchParams.append('gln', formControl.value);    

    
    formControl.loading = true;
    return this.httpClient.get(`${baseUrl}?${searchParams.toString()}`)
    .pipe(
      map((result: any) => {
        return !result || result?.status === 'A' ? { glnInvalid: true } : null
      }),
      catchError(() => {
        formControl.loading = false;
        return of({ glnInvalid: true })
      }),
      finalize(() => {
        formControl.loading = false
      })
    );
  }

  public avsChecksumValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let avsNumberString: string = control.value;
      if (avsNumberString) {
        avsNumberString = avsNumberString.split('.').join('');
        const avsNumberArray = avsNumberString
          .split('')
          .map((stringDigit: string) => parseInt(stringDigit, 10));

        if (avsNumberArray.length === 13) {
          const controlDigit = avsNumberArray[12];
          avsNumberArray.pop();
          const sumOdds = avsNumberArray
            .filter((v, i) => !(i % 2))
            .reduce((a, b) => a + b);
          const sumEvens = avsNumberArray
            .filter((v, i) => i % 2)
            .reduce((a, b) => a + b);

          const checksum = 3 * sumEvens + sumOdds;
          const calculatedControlDigit =
            checksum % 10 > 0 ? 10 - (checksum % 10) : 0;
          if (calculatedControlDigit !== controlDigit) {
            return {
              avsControlDigitInvalid: true,
            };
          }
        }
      }
      return null;
    };
  }

  public cantonCaraValidator(): ValidatorFn {
    return (control:AbstractControl) : ValidationErrors | null => {
        const value = control.value;

      if (!value) {
            return null;
        }

      return value === 'Autre' ? { NotCantonCara: true } : null;
    }
  }


  public getUserLanguage(){
    const codeLanguage = this.translateService.currentLang;
    return codeLanguage != null ? getAcceptedLanguageEnumFromLanguageCode(codeLanguage) : AcceptedLanguage.FRENCH;
  }

}
